import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { PageHeader } from "../components/PageHeader";
import { CTABanner } from "../components/CTABanner";
import { SEO } from "../components/SEO";
import { FAQS, MAPS_EMBED_URL, MAPS_LINK } from "../data/siteData";

const spring = { type: "spring", stiffness: 200, damping: 20 };

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.source ? `${faq.answer} ${faq.source}` : faq.answer,
    },
  })),
};

export default function FAQ() {
  return (
    <div data-testid="faq-page">
      <SEO
        title="FAQ | Thrive Pediatric Therapy, Nassau"
        description="Answers to common parent questions about pediatric therapy at Thrive in Nassau, Bahamas - referrals, ages, first appointments, sessions and booking."
        jsonLd={FAQ_SCHEMA}
      />
      <PageHeader
        testId="faq-header"
        eyebrow="FAQ"
        title="Frequently Asked Questions"
        description="Everything parents usually ask before their first visit. Can't find what you're looking for? Send us a message, we love to chat."
      />

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={spring}>
          <Accordion type="single" collapsible data-testid="faq-accordion" className="space-y-4">
            {FAQS.map((faq, i) => (
              <AccordionItem
                key={faq.question}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                className="bg-white border border-border rounded-3xl px-6 sm:px-8 overflow-hidden transition-shadow duration-300 data-[state=open]:shadow-lg data-[state=open]:shadow-navy/5"
              >
                <AccordionTrigger
                  data-testid={`faq-question-${i}`}
                  className="font-display text-base sm:text-lg font-semibold text-navy text-left py-6 hover:no-underline hover:text-teal-dark transition-colors"
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent data-testid={`faq-answer-${i}`} className="text-sm sm:text-base text-navy-light leading-relaxed pb-6">
                  {faq.answer}
                  {faq.source && (
                    <em className="block mt-3 text-xs sm:text-sm text-navy-light/80">{faq.source}</em>
                  )}
                  {faq.map && (
                    <div data-testid={`faq-map-${i}`} className="mt-4">
                      <div className="rounded-2xl overflow-hidden border border-border">
                        <iframe
                          title="Thrive Pediatric Therapy location map - 11 Mount Royal Ave, Nassau"
                          src={MAPS_EMBED_URL}
                          className="w-full h-64 border-0"
                          loading="lazy"
                          allowFullScreen
                          referrerPolicy="no-referrer-when-downgrade"
                        />
                      </div>
                      <a
                        href={MAPS_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`faq-map-link-${i}`}
                        className="mt-3 inline-flex items-center gap-2 font-extrabold text-sm text-teal-dark hover:underline"
                      >
                        Open in Maps
                      </a>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </section>

      <CTABanner title="Still curious?" />
    </div>
  );
}
